import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { findCity, RIVIERA_CITIES } from "@/lib/riviera";
import { usePageMeta } from "@/lib/seo";
import { RivieraHero, MonacoBadge, CityCard, BTN_AZURE, BTN_GHOST, EYEBROW_AZ, H2 } from "@/components/riviera/RivieraUI";

export default function RivieraCity() {
  const { city: slug } = useParams();
  const { t } = useTranslation();
  const city = findCity(slug);
  usePageMeta(city ? `${t(`riviera.cities.${city.slug}.headline`)} | Trustline Services France` : null, city ? t(`riviera.cities.${city.slug}.intro`) : "", city ? { canonicalPath: `/fr/${city.slug}` } : {});
  if (!city) return <Navigate to="/fr" replace />;
  const k = `riviera.cities.${city.slug}`;
  const ctaLabel = t("riviera.city_page.monaco_cta");
  const ctaTestid = city.inquiryOnly ? "monaco-inquiry-button" : "city-inquiry-button";
  const inquiryUrl = `/fr/inquiry?city=${city.slug}`;
  const bookUrl = `/fr/book?city=${city.slug}`;
  const ctas = (sfx) => city.inquiryOnly
    ? <Link to={inquiryUrl} className={BTN_AZURE} data-testid={sfx ? `${ctaTestid}-${sfx}` : ctaTestid}>{ctaLabel}</Link>
    : (
      <>
        <Link to={bookUrl} className={BTN_AZURE} data-testid={sfx ? `city-book-button-${sfx}` : "city-book-button"}>{t("riviera.cta_book")}</Link>
        <Link to={inquiryUrl} className={BTN_GHOST} data-testid={sfx ? `city-inquiry-button-${sfx}` : ctaTestid}>{t("riviera.cta_inquiry")}</Link>
      </>
    );

  return (
    <div data-testid={`riviera-city-${city.slug}`}>
      <RivieraHero img={city.image} eyebrow={`${t("country.fr")} · ${city.name}`} title={t(`${k}.headline`)} sub={t(`${k}.intro`)}
        badge={city.inquiryOnly ? <MonacoBadge t={t} /> : null}>
        {ctas()}
      </RivieraHero>

      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <Link to="/fr" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3A6B] hover:text-[#0EA5E9]" data-testid="city-back-link">
          <ArrowLeft className="w-4 h-4" /> {t("riviera.city_page.back")}
        </Link>
      </div>

      {city.inquiryOnly && (
        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-6">
          <div className="rounded-2xl bg-amber-50 ring-1 ring-amber-200 p-5 text-sm text-amber-900" data-testid="monaco-pending-note">{t("riviera.monaco_note")}</div>
        </div>
      )}

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className={EYEBROW_AZ}>{city.name}</p>
            <h2 className={H2}>{t("riviera.city_page.highlights_title", { city: city.name })}</h2>
            <p className="mt-4 text-sm text-slate-500">{t("riviera.no_price")}</p>
          </div>
          <ul className="lg:col-span-7 grid gap-4">
            {(t(`${k}.highlights`, { returnObjects: true }) || []).map((h, i) => (
              <li key={i} className="flex gap-3 items-start rounded-2xl bg-white ring-1 ring-slate-200 p-5" data-testid={`city-highlight-${i}`}>
                <CheckCircle2 className="w-5 h-5 text-[#0EA5E9] shrink-0 mt-0.5" /><span className="font-semibold text-[#0F172A]">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#F0F9FF]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <h2 className="text-3xl sm:text-4xl text-[#0F172A] text-center">{t("riviera.city_page.faq_title", { city: city.name })}</h2>
          <Accordion type="single" collapsible className="mt-8 bg-white rounded-2xl ring-1 ring-sky-100 px-6">
            {(t(`${k}.faq`, { returnObjects: true }) || []).map((f, i) => (
              <AccordionItem key={i} value={`q${i}`} data-testid="faq-accordion-item">
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="pt-16 md:pt-24">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="bg-[#102547] rounded-3xl px-8 py-14 md:px-14 text-white relative overflow-hidden">
            <div className="absolute -right-16 -top-16 w-72 h-72 bg-[#0EA5E9]/25 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl max-w-2xl">{t("riviera.city_page.cta_title", { city: city.name })}</h2>
              <p className="mt-3 text-slate-300 max-w-xl">{t("riviera.city_page.cta_sub")}</p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="city-cta-bottom">{ctas("bottom")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {RIVIERA_CITIES.filter((c) => c.slug !== city.slug).map((c, i) => <CityCard key={c.slug} t={t} city={c} i={i} />)}
        </div>
      </section>
    </div>
  );
}
